import { QuestionCircleOutlined } from "@ant-design/icons";
import { Progress, Tabs, Tooltip, Typography } from "antd";
import { useGetBalanceTotalsQuery, useGetBalanceWalletQuery } from "api/TenantSlug/Balance/hooks.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useState } from "react";
import { BalanceRole } from "types/enums";
import { BALANCE_BUCKETS, CIRCLE_RADIUS } from "utils/balanceBuckets.ts";
import { formatCurrency } from "utils/formatCurrency.ts";

export const AffiliateBalanceTotals = () => {
    const { t } = usePaldockTranslation("components.affiliate-balance-totals");
    // Bucket labels/tooltips are shared with HeaderWallet on purpose — same buckets, same wording.
    const { t: tBucket } = usePaldockTranslation("components.header-wallet");
    const [activeCurrencyCode, setActiveCurrencyCode] = useState<string>("");

    const getBalanceWalletQuery = useGetBalanceWalletQuery();
    const getBalanceTotalsQuery = useGetBalanceTotalsQuery({ role: BalanceRole.Affiliate });

    const affiliateWallet = getBalanceWalletQuery.data?.roles.find((role) => role.role === BalanceRole.Affiliate);
    const currencies = getBalanceTotalsQuery.data?.currencies ?? [];

    if (!affiliateWallet || currencies.length === 0) {
        return <Typography.Text type="secondary">{t("empty")}</Typography.Text>;
    }

    const activeCurrency =
        currencies.find((currency) => currency.currency.currency_code === activeCurrencyCode) ?? currencies[0];
    const format = (amount: number) => formatCurrency(amount, activeCurrency.currency.currency_code);
    const total = BALANCE_BUCKETS.reduce((sum, bucket) => sum + activeCurrency[bucket.key], 0);

    // Same stacked-Progress technique as HeaderWallet — see that component for why.
    const cumulativePercents = BALANCE_BUCKETS.reduce<number[]>((acc, bucket) => {
        const previous = acc[acc.length - 1] ?? 0;
        const share = total > 0 ? (activeCurrency[bucket.key] / total) * 100 : 0;
        acc.push(Math.min(previous + share, 100));
        return acc;
    }, []);

    return (
        <div className="flex flex-col w-full">
            <Typography.Title level={4} className="!mb-4">
                {t("title", {
                    amount: formatCurrency(affiliateWallet.available, affiliateWallet.currency.currency_code),
                })}
            </Typography.Title>

            <Tabs
                activeKey={activeCurrency.currency.currency_code}
                onChange={setActiveCurrencyCode}
                items={currencies.map((currency) => ({
                    key: currency.currency.currency_code,
                    label: currency.currency.currency_code,
                }))}
            />

            <div className="flex flex-wrap items-center gap-6 mt-3">
                {BALANCE_BUCKETS.map(({ key, color, textColor, bold, tooltip }) => (
                    <div key={key} className="flex items-center gap-2">
                        <div style={{ width: 12, height: 12, backgroundColor: color, ...CIRCLE_RADIUS }} />
                        <span style={{ color: textColor }}>{tBucket(key)}</span>
                        {tooltip && (
                            <Tooltip title={tBucket(`${key}_tooltip`)}>
                                <QuestionCircleOutlined style={{ fontSize: 10, color: textColor }} />
                            </Tooltip>
                        )}
                        <span className={bold ? "font-semibold" : ""} style={{ color: textColor }}>
                            {format(activeCurrency[key])}
                        </span>
                    </div>
                ))}
            </div>

            <Tooltip
                title={BALANCE_BUCKETS.map(({ key }) => `${tBucket(key)}: ${format(activeCurrency[key])}`).join(" / ")}
            >
                <div className="relative mt-3" style={{ height: 16 }}>
                    {BALANCE_BUCKETS.map(({ key, color }, index) => (
                        <Progress
                            key={key}
                            percent={cumulativePercents[index]}
                            showInfo={false}
                            strokeColor={color}
                            trailColor="transparent"
                            size={["100%", 16]}
                            className="absolute inset-0"
                        />
                    )).reverse()}
                </div>
            </Tooltip>
        </div>
    );
};
